import React, {ChangeEvent, FC, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import StepInfo from "pages/StepPages/components";
import {Button, SortBy} from "components/shared";
import {Search} from "components-new";
import {OptionInterface} from "components/shared/SortBy/SortBy";
import Instruction, {INSTRUCTION_STATE_TYPES} from "./Instruction/Instruction";
import Table from "./Table/Table";
import TableGroups from "./TableGroups/TableGroups";
import {ReactComponent as SvgUsualTable} from "images/table.svg";
import {ReactComponent as SvgGroupTable} from "images/groups.svg";
import useDebounce from "../../../hooks/useDebounce";
import {useAppDispatch, useAppSelector} from "toolkit/hooks";
import {setData, setSearch, setSortBy, setStatusFilter, updateData,} from "toolkit/steps/stepFive/stepFive.slice";
import cn from "classnames";
import styles from "./StepFive.module.sass";
import {usePaginationWithIntersection} from "hooks/usePaginationWithIntersection";
import {BUTTON_VARIANTS} from "../../../components/shared/Button/Button";
import {ModalCreateTemplate} from "./SelectionCategories/ModalCreateTemplate";

const options: OptionInterface[] = [
	{ name: "0", text: "Все" },
	{ name: "1", text: "Модель угроз сформирована" },
	{ name: "2", text: "Категория присвоена" },
	{ name: "3", text: "Ожидает формирования модели угроз" },
	{ name: "4", text: "Повторное категорирование" },
];

const StepFive: FC = () => {
	const dispatch = useAppDispatch();

	const { organizationId } = useParams();
	const { data, search, sortBy, statusFilter } = useAppSelector((state) => state.stepFive);

	const [objectsParams, setObjectsParams] = useState<{
		activity_type_filter: number;
		obj_type_filter: number;
		docs_approval_type_filter: number;
		status_filter: number;
		obj_name: string;
		page: number;
		num: number;
	}>({
		activity_type_filter: sortBy.activities.id,
		obj_type_filter: sortBy.objects.id,
		docs_approval_type_filter: sortBy.statuses.id,
		status_filter: statusFilter.name,
		obj_name: search,
		page: 1,
		num: 10,
	});

	const firstLoad = useRef(true);

	const [isTableGroupVariant, setIsTableGroupVariant] = useState(false);

	const objectsRef = useRef<HTMLDivElement | null>(null);
	const objectsGroupRef = useRef<HTMLDivElement | null>(null);

	const lastTargetRef = useMemo(() => {
		return isTableGroupVariant ? objectsRef : objectsGroupRef;
	}, [isTableGroupVariant]);

	const selectedOrganization = useAppSelector((state) => state.organizations.selectedOrganization);

	const [sureModalVisible, setSureModalVisible] = useState<boolean>(false);

	const debouncedValue = useDebounce(search, 500);

	const navigate = useNavigate();

	useEffect(() => {
		setObjectsParams((prevState) => ({ ...prevState, page: 1, obj_name: debouncedValue }));
		setIsFinally(false);
		setFetch(true);
		//eslint-disable-next-line
	}, [debouncedValue]);

	useEffect(() => {
		setObjectsParams((prevState) => ({
			...prevState,
			page: 1,
			activity_type_filter: sortBy.activities.id,
			obj_type_filter: sortBy.objects.id,
			docs_approval_type_filter: sortBy.statuses.id,
		}));
		setIsFinally(false);
		setFetch(true);
		//eslint-disable-next-line
	}, [sortBy]);

	const onClickModelFormed = (id: number, objectName: string, row: any) => {
		navigate(`/categorization/newModel/${organizationId}/${id}`, {
			state: {
				objectName,
				docsId: { actId: row.actId, resId: row.resId },
			},
		});
	};

	const handleObjects = (array: any[]) => {
		if (objectsParams.page === 1) {
			if (array.length) {
				if (!(array.length < objectsParams.num)) {
					setObjectsParams((prevState) => ({
						...prevState,
						page: prevState.page + 1,
					}));
				} else {
					setIsFinally(true);
				}
			} else {
				setIsFinally(true);
			}
			dispatch(setData(array));
		} else {
			if (array.length) {
				if (!(array.length < objectsParams.num)) {
					setObjectsParams((prevState) => ({
						...prevState,
						page: prevState.page + 1,
					}));
				} else {
					setIsFinally(true);
				}
				dispatch(updateData(array));
			}else{
				setIsFinally(true);
			}
		}
	};

	const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
		dispatch(setSearch(e.target.value));
	};

	const handleBy = (sort: any, type: any) => {
		dispatch(setSortBy({ sort, type }));
	};

	const handleStatus = (option: any) => {
		dispatch(setStatusFilter(option));
		setObjectsParams((prevState) => ({ ...prevState, status_filter: option.name, page: 1 }));
		setIsFinally(false);
		setFetch(true);
	};

	useEffect(() => {
		if (firstLoad.current) firstLoad.current = false;
	}, []);

	const { setIsFinally, setFetch } = usePaginationWithIntersection({
		link: `/objects_kii/${organizationId}/list`,
		then: handleObjects,
		ref: { current: lastTargetRef.current?.parentElement },
		params: objectsParams,
		lastTarget: lastTargetRef,
		dependencies: [
			statusFilter,
			sortBy,
			!firstLoad.current,
			lastTargetRef.current?.parentElement,
			lastTargetRef.current,
		],
	});

	const [patternModal, setPatternModal] = useState<{
		active: boolean;
		data: number | null;
		mode: "create" | "select";
	}>({
		active: false,
		mode: "select",
		data: null,
	});

	const closePatternModal = () => setPatternModal((prevState) => ({ ...prevState, active: false, data: null }));


	return (
		<div className={styles.categorization}>
			<ModalCreateTemplate target={patternModal} close={closePatternModal} />

			<div className={styles.header}>
				<StepInfo
					stepTitle={"5. Категорирование ОКИИ"}
					info="Выберите объект КИИ для подбора категории значимости"
					companyName={selectedOrganization?.fullName}
				/>

				<div className={styles.right}>
					<div style={{ display: "flex", alignItems: "center" }}>
						<div className={styles.label}>Фильтровать по</div>
						<SortBy options={options} selectedOption={statusFilter} setSelectedOption={handleStatus} />
					</div>

					<div>
						<Search className={styles.search} searchValue={search} handleSearch={handleSearch} />
					</div>

					<div>
						<Button variant={BUTTON_VARIANTS.OUTLINED} onClick={()=>setPatternModal({active:true,data:null,mode:'select'})}>Применить шаблон</Button>
					</div>

					<div>
						<Button to={`/categorization/documents/${organizationId}`}>Показать документы</Button>
					</div>

					<div>
						<SvgUsualTable
							onClick={() => setIsTableGroupVariant(false)}
							className={cn(styles.tableIcon, {
								[styles.tableIconActive]: !isTableGroupVariant,
							})}
						/>
						<SvgGroupTable
							onClick={() => setIsTableGroupVariant(true)}
							className={cn(styles.tableIcon, {
								[styles.tableIconActive]: isTableGroupVariant,
							})}
						/>
					</div>
				</div>
			</div>

			{isTableGroupVariant ? (
				<>
					<Instruction type={INSTRUCTION_STATE_TYPES.SQUARES} />
					<TableGroups
						data={data}
						onClickModelFormed={onClickModelFormed}
						sureModalVisible={sureModalVisible}
						showSureModal={() => setSureModalVisible(true)}
						close={() => setSureModalVisible(false)}
						ref={objectsGroupRef}
						lastTargetRef={lastTargetRef}
					/>
				</>
			) : (
				<>
					<Instruction />
					<Table
						data={data}
						handleBy={handleBy}
						onClickModelFormed={onClickModelFormed}
						sureModalVisible={sureModalVisible}
						showSureModal={() => setSureModalVisible(true)}
						close={() => setSureModalVisible(false)}
						ref={objectsRef}
						lastTargetRef={lastTargetRef}
					/>
				</>
			)}
		</div>
	);
};

export default StepFive;
